import React, { useEffect, useRef, useState } from "react";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined
} from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Tag,
  Tooltip
} from "antd";
import { fetchSprTov } from "@components/health/clients/health-clients-api";
import { pick, toNumber } from "@components/health/clients/health-clients-utils";
import {
  CNT_IS_AUTO_LABELS,
  CNT_IS_AUTO_TAG_COLOR,
  emptyCnt,
  formatDate,
  formatMoney,
  isCntDeletable,
  isCntEditable
} from "./doc-exp-utils";

const normalizeTovOption = (row) => {
  const idTov = toNumber(pick(row, "idTov", "IdTov"));
  const name = pick(row, "name", "Name") ?? "";
  const zena = toNumber(pick(row, "zenaExp", "ZenaExp", "zena", "Zena"));
  return {
    value: idTov,
    label: name,
    zenaExp: zena ?? 0
  };
};

const CntSummary = ({ cnt, index }) => {
  const c = cnt || {};
  const isAuto = toNumber(c.isAuto) ?? 0;
  const catLabel = CNT_IS_AUTO_LABELS[isAuto] || "Дополнительно";
  const catColor = CNT_IS_AUTO_TAG_COLOR[isAuto] || "default";
  const name = (c.tovName || "").trim() || `Товар ${index + 1}`;
  const money = [
    c.zenaExp != null ? formatMoney(c.zenaExp) : "",
    c.kol != null ? `× ${c.kol}` : "",
    c.symaExp != null ? `= ${formatMoney(c.symaExp)}` : ""
  ]
    .filter(Boolean)
    .join(" ");
  const plat = Number(c.symaPlat) || 0;
  const meta = [
    formatDate(c.fdate),
    money,
    Math.abs(plat) >= 0.005 ? `оплачено ${formatMoney(plat)}` : ""
  ]
    .filter(Boolean)
    .join(" · ");
  const comment = (c.komment || "").trim();

  return (
    <div className="doc-exp-card__cnt-summary">
      <div className="doc-exp-card__cnt-summary-row doc-exp-card__cnt-summary-row--main">
        <Tag className="doc-exp-card__cnt-summary-cat" color={catColor}>
          {catLabel}
        </Tag>
        <span className="doc-exp-card__cnt-summary-name">{name}</span>
      </div>
      {meta ? (
        <div className="doc-exp-card__cnt-summary-row doc-exp-card__cnt-summary-row--meta">{meta}</div>
      ) : null}
      {comment ? (
        <div className="doc-exp-card__cnt-summary-row doc-exp-card__cnt-summary-row--comment">
          {comment}
        </div>
      ) : null}
    </div>
  );
};

const CntDolg = ({ cnt }) => {
  const dolg = Number(cnt?.symaDolg) || 0;
  const hasDolg = Math.abs(dolg) >= 0.005;
  return (
    <span
      className={`doc-exp-card__cnt-summary-dolg${hasDolg ? " doc-exp-card__cnt-summary-dolg--warn" : ""}`}
    >
      <span className="doc-exp-card__cnt-summary-dolg-label">Долг</span>
      {formatMoney(dolg)}
    </span>
  );
};

const snapshotCntValues = (cnt) => {
  if (!cnt) return emptyCnt();
  return {
    ...cnt,
    fdate: cnt.fdate && typeof cnt.fdate.clone === "function" ? cnt.fdate.clone() : cnt.fdate
  };
};

const DocExpCntsFields = ({
  fields,
  add,
  remove,
  form,
  cntsWatch,
  cntsReadOnly,
  formItemLayout,
  editingKey,
  setEditingKey,
  expandLastRef,
  saving,
  onConfirmEdit
}) => {
  const [tovOptions, setTovOptions] = useState([]);
  const [tovLoading, setTovLoading] = useState(false);
  const tovSearchRef = useRef(0);
  const tovTimerRef = useRef(0);
  const editSnapshotRef = useRef(null);

  const beginEdit = (field) => {
    const values = form.getFieldValue(["cnts", field.name]);
    editSnapshotRef.current = {
      key: field.key,
      name: field.name,
      values: snapshotCntValues(values),
      isNew: !(values?.idStr > 0)
    };
    setEditingKey(field.key);
  };

  const cancelEdit = (field) => {
    const snap = editSnapshotRef.current;
    if (snap && snap.key === field.key) {
      if (snap.isNew) {
        remove(field.name);
      } else {
        form.setFieldValue(["cnts", field.name], snapshotCntValues(snap.values));
      }
    }
    editSnapshotRef.current = null;
    setEditingKey(null);
  };

  useEffect(() => {
    if (!expandLastRef.current || fields.length === 0) return;
    expandLastRef.current = false;
    const last = fields[fields.length - 1];
    beginEdit(last);
  }, [fields, expandLastRef, setEditingKey]);

  const loadTov = (q) => {
    clearTimeout(tovTimerRef.current);
    tovTimerRef.current = setTimeout(() => {
      const requestId = ++tovSearchRef.current;
      setTovLoading(true);
      fetchSprTov(q)
        .then((data) => {
          if (requestId !== tovSearchRef.current) return;
          setTovOptions(Array.isArray(data) ? data.map(normalizeTovOption).filter((o) => o.value) : []);
        })
        .catch(() => {
          if (requestId !== tovSearchRef.current) return;
          setTovOptions([]);
        })
        .finally(() => {
          if (requestId === tovSearchRef.current) setTovLoading(false);
        });
    }, 300);
  };

  useEffect(() => {
    if (editingKey == null) return;
    loadTov("");
  }, [editingKey]);

  return (
    <div className="doc-exp-card__cnts">
      {fields.length === 0 ? (
        <div className="doc-exp-card__cnts-empty">Нет товаров</div>
      ) : null}
      {fields.map((field, index) => {
        const cnt = cntsWatch[index] || {};
        const editing = editingKey === field.key;
        const editable = isCntEditable(cnt) && !cntsReadOnly;
        const title = (cnt.tovName || "").trim() || `Товар ${index + 1}`;
        const idTov = cnt.idTov;
        const options =
          idTov && !tovOptions.some((o) => o.value === idTov)
            ? [{ value: idTov, label: cnt.tovName || `#${idTov}`, zenaExp: cnt.zenaExp }, ...tovOptions]
            : tovOptions;

        return (
          <div
            className={`doc-exp-card__cnt-block${editing ? " doc-exp-card__cnt-block--open" : ""}`}
            key={field.key}
          >
            <div className="doc-exp-card__cnt-block-head">
              <div className="doc-exp-card__cnt-block-summary">
                {editing ? (
                  <span className="doc-exp-card__cnt-block-title" title={title}>
                    {editable ? "Редактирование" : title}
                  </span>
                ) : (
                  <CntSummary cnt={cnt} index={index} />
                )}
              </div>
              <div className="doc-exp-card__cnt-block-trail">
              {(editing || editable) ? (
              <Space size={0} className="doc-exp-card__cnt-block-actions">
                {editing ? (
                  <>
                    <Tooltip title="Отменить">
                      <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        disabled={saving}
                        onClick={() => cancelEdit(field)}
                        aria-label="Отменить"
                      />
                    </Tooltip>
                    {editable ? (
                      <Tooltip title="Подтвердить">
                        <Button
                          type="text"
                          size="small"
                          icon={<CheckOutlined />}
                          loading={saving}
                          disabled={saving}
                          onClick={async () => {
                            const ok = await onConfirmEdit?.(field.name);
                            if (ok) {
                              editSnapshotRef.current = null;
                              setEditingKey(null);
                            }
                          }}
                          aria-label="Подтвердить"
                        />
                      </Tooltip>
                    ) : null}
                  </>
                ) : (
                  <>
                    <Tooltip title="Изменить">
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        disabled={saving}
                        onClick={() => beginEdit(field)}
                        aria-label="Изменить"
                      />
                    </Tooltip>
                    <Tooltip
                      title={
                        isCntDeletable(cnt)
                          ? "Удалить"
                          : "Нельзя удалить: по строке есть разнесение"
                      }
                    >
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        disabled={saving || !isCntDeletable(cnt)}
                        onClick={() => {
                          if (editingKey === field.key) {
                            editSnapshotRef.current = null;
                            setEditingKey(null);
                          }
                          remove(field.name);
                        }}
                        aria-label="Удалить товар"
                      />
                    </Tooltip>
                  </>
                )}
              </Space>
              ) : null}
              <CntDolg cnt={cnt} />
              </div>
            </div>

            <div
              className="doc-exp-card__cnt-block-body"
              hidden={!editing}
              aria-hidden={!editing}
            >
              <Form.Item name={[field.name, "idStr"]} hidden>
                <InputNumber />
              </Form.Item>
              <Form.Item name={[field.name, "isAuto"]} hidden>
                <InputNumber />
              </Form.Item>
              <Form.Item name={[field.name, "tovName"]} hidden>
                <Input />
              </Form.Item>
              <Form.Item name={[field.name, "symaExp"]} hidden>
                <InputNumber />
              </Form.Item>
              <Form.Item name={[field.name, "symaDiscount"]} hidden>
                <InputNumber />
              </Form.Item>
              <Form.Item name={[field.name, "symaPlat"]} hidden>
                <InputNumber />
              </Form.Item>
              <Form.Item name={[field.name, "symaDolg"]} hidden>
                <InputNumber />
              </Form.Item>
              <Form.Item
                {...formItemLayout}
                label="Услуга"
                name={[field.name, "idTov"]}
                rules={editable ? [{ required: true, message: "Укажите услугу" }] : undefined}
              >
                <Select
                  className="doc-exp-card__cnt-tov-select"
                  showSearch
                  allowClear
                  filterOption={false}
                  loading={tovLoading}
                  options={options}
                  disabled={!editable}
                  placeholder="Поиск услуги"
                  style={{ width: "100%", maxWidth: "100%" }}
                  popupMatchSelectWidth
                  onSearch={loadTov}
                  onChange={(value, option) => {
                    const zena = option?.zenaExp;
                    form.setFieldValue(["cnts", field.name, "tovName"], option?.label || "");
                    if (zena != null && Number(zena) > 0) {
                      form.setFieldValue(["cnts", field.name, "zenaExp"], zena);
                    }
                  }}
                />
              </Form.Item>
              <Form.Item {...formItemLayout} label="Дата" name={[field.name, "fdate"]}>
                <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} disabled={!editable} />
              </Form.Item>
              <Form.Item {...formItemLayout} label="Цена" name={[field.name, "zenaExp"]}>
                <InputNumber min={0} style={{ width: "100%" }} disabled={!editable} />
              </Form.Item>
              <Form.Item {...formItemLayout} label="Количество" name={[field.name, "kol"]}>
                <InputNumber min={0} style={{ width: "100%" }} disabled={!editable} />
              </Form.Item>
              <Form.Item {...formItemLayout} label="% скидки" name={[field.name, "prcDiscount"]}>
                <InputNumber min={0} max={100} style={{ width: "100%" }} disabled={!editable} />
              </Form.Item>
              <Form.Item {...formItemLayout} label="Долг">
                <InputNumber
                  value={cnt.symaDolg ?? 0}
                  precision={2}
                  style={{ width: "100%" }}
                  disabled
                />
              </Form.Item>
              <Form.Item {...formItemLayout} label="Комментарий" name={[field.name, "komment"]}>
                <Input.TextArea rows={2} disabled={!editable} />
              </Form.Item>
            </div>
          </div>
        );
      })}
      {!cntsReadOnly ? (
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => {
            expandLastRef.current = true;
            add(emptyCnt());
          }}
          block
          className="doc-exp-card__cnt-add"
        >
          Добавить товар
        </Button>
      ) : null}
    </div>
  );
};

/**
 * Список товаров путёвки: сокращённый блок, правка только IS_AUTO = 0.
 */
const DocExpCntsPanel = ({
  form,
  cntsWatch,
  cntsReadOnly,
  formItemLayout,
  resetKey,
  saving,
  onConfirmEdit
}) => {
  const [editingKey, setEditingKey] = useState(null);
  const expandLastRef = useRef(false);

  useEffect(() => {
    setEditingKey(null);
    expandLastRef.current = false;
  }, [resetKey]);

  return (
    <Form.List name="cnts">
      {(fields, { add, remove }) => (
        <DocExpCntsFields
          fields={fields}
          add={add}
          remove={remove}
          form={form}
          cntsWatch={cntsWatch}
          cntsReadOnly={cntsReadOnly}
          formItemLayout={formItemLayout}
          editingKey={editingKey}
          setEditingKey={setEditingKey}
          expandLastRef={expandLastRef}
          saving={saving}
          onConfirmEdit={onConfirmEdit}
        />
      )}
    </Form.List>
  );
};

export default DocExpCntsPanel;
